import React, {useEffect, useState} from 'react';
import {socket} from '../../../socket';
import userStore from '../../../zustand/store/userStore';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import { fetchConversationsAPI, startNewChatAPI } from '../../../zustand/API';

const ChatLayout = () => {
	const {user} = userStore();
	const [conversations, setConversations] = useState([]);
	const [selectedChat, setSelectedChat] = useState(null);
	const [notifications, setNotifications] = useState({});
	const [newEmail, setNewEmail] = useState('');
	const [showSidebar, setShowSidebar] = useState(false);

	const fetchConversations = async () => {
		if (!user?.email) return;
		try {
			const data = await fetchConversationsAPI(user.email);
			const sorted = data.sort(
				(a, b) =>
					new Date(b.updatedAt || b.lastMessage?.createdAt) -
					new Date(a.updatedAt || a.lastMessage?.createdAt),
			);
			setConversations(sorted);
		} catch (err) {
			console.error('Failed to load conversations', err.message);
		}
	};

	useEffect(() => {
		if (!user?.email) return;

		socket.emit('register', user.email);
		fetchConversations();

		socket.on('refreshConversations', fetchConversations);
		return () => socket.off('refreshConversations', fetchConversations);
	}, [user?.email]);

	useEffect(() => {
		if (!conversations.length) return;
		conversations.forEach((c) => c?._id && socket.emit('join', c._id));
	}, [conversations]);

	useEffect(() => {
		const handleNewMessage = ({chatId, message}) => {
			if (chatId !== selectedChat?._id) {
				setNotifications((prev) => ({
					...prev,
					[chatId]: (prev[chatId] || 0) + 1,
				}));
			}
			setConversations((prev) =>
				prev
					.map((c) =>
						c._id === chatId
							? {
									...c,
									messages: [...(c.messages || []), message],
									updatedAt: new Date(),
							  }
							: c,
					)
					.sort(
						(a, b) =>
							new Date(b.updatedAt || b.lastMessage?.timestamp || 0) -
							new Date(a.updatedAt || a.lastMessage?.timestamp || 0),
					),
			);
		};

		socket.on('newMessage', handleNewMessage);
		return () => socket.off('newMessage', handleNewMessage);
	}, [selectedChat?._id]);

	const openChat = (chat) => {
		setSelectedChat(chat);
		setNotifications((prev) => {
			const {[chat._id]: _, ...rest} = prev;
			return rest;
		});
		setShowSidebar(false);
	};

	const startNewChat = async () => {
		if (!newEmail.trim() || newEmail === user?.email) return;

		try {
			const data = await startNewChatAPI(user.email, newEmail);

			if (!conversations.find((c) => c._id === data._id)) {
				setConversations((prev) => [data, ...prev]);
			}

			setSelectedChat(data);
			setNewEmail('');
			setShowSidebar(false);
		} catch (err) {
			console.error('Error starting new chat:', err.message);
		}
	};
	return (
		<div className="flex flex-col h-screen bg-gray-100">
			<div className="bg-indigo-600 text-white py-3 px-4 flex justify-between items-center">
				<div className="text-sm font-medium">
					For any query contact admin:{' '}
					<span className="text-orange-950">academysystem@gmail.com</span>
				</div>
				<button
					className="sm:hidden p-2 text-white text-xl"
					onClick={() => setShowSidebar(true)}
				>
					☰
				</button>
			</div>

			<div className="flex flex-1 overflow-hidden relative">
				{showSidebar && (
					<div
						className="fixed inset-0 bg-black bg-opacity-40 z-40 sm:hidden"
						onClick={() => setShowSidebar(false)}
					/>
				)}

				<div
					className={`${
						showSidebar ? 'block' : 'hidden'
					} fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-300 p-4 sm:static sm:block sm:w-72 sm:p-0 flex flex-col`}
				>
					<div className="p-2 flex items-center gap-2">
						<input
							type="email"
							placeholder="Enter email"
							value={newEmail}
							onChange={(e) => setNewEmail(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && startNewChat()}
							className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md outline-none"
						/>
						<button
							onClick={startNewChat}
							className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-500 transition"
						>
							Start
						</button>
					</div>

					<div className="overflow-y-auto flex-1">
						<ConversationList
							conversations={conversations}
							openChat={openChat}
							notifications={notifications}
							currentUserEmail={user?.email}
						/>
					</div>
				</div>

				<div className="flex-1">
					{selectedChat ? (
						<ChatWindow chat={selectedChat} currentUserEmail={user?.email} />
					) : (
						<div className="h-full flex items-center justify-center text-gray-400 text-lg">
							Select a conversation
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ChatLayout;
